/**
 * CMS 섹션 폼 — dynamic section 별 추가 설정 토글.
 *
 * board.preview 설정은 서버가 component('select')로 모든 board별 필터 패널을
 * 렌더하고, 여기서는 선택된 board 패널만 활성화한다.
 */
export function initCmsSectionForm(scope: Document | HTMLElement = document): void {
    const forms =
        scope instanceof HTMLElement && scope.matches?.("[data-cms-section-form]")
            ? [scope]
            : Array.from(
                  (scope as ParentNode).querySelectorAll<HTMLElement>(
                      "[data-cms-section-form]",
                  ),
              );

    forms.forEach((form) => {
        if (form.dataset.cmsSectionFormInited === "1") return;
        form.dataset.cmsSectionFormInited = "1";

        const typeSelect = form.querySelector<HTMLSelectElement>(
            'select[name="section_type"]',
        );
        const dynamicSelect = form.querySelector<HTMLSelectElement>(
            'select[name="dynamic_key"]',
        );
        const boardSettings = form.querySelector<HTMLElement>(
            "[data-board-preview-settings]",
        );
        const boardSelect = form.querySelector<HTMLSelectElement>(
            "[data-board-preview-board]",
        );
        const panels = Array.from(
            form.querySelectorAll<HTMLElement>("[data-board-preview-filter-panel]"),
        );
        const collectionFields = readCollectionFields(form);

        const selectedType = (): string => typeSelect?.value || "";
        const selectedDynamic = (): string => dynamicSelect?.value || "";

        const applyBoard = (): void => {
            const boardId = boardSelect?.value || "";
            panels.forEach((panel) => {
                const active = boardId !== "" && panel.dataset.boardId === boardId;
                panel.hidden = !active;
                panel
                    .querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
                        "[data-board-preview-filter-input]",
                    )
                    .forEach((input) => {
                        input.disabled = !active;
                    });
            });
            renderFieldBrowser(form, boardId, collectionFields);
        };

        const apply = (): void => {
            const showBoardPreview =
                selectedType() === "dynamic" && selectedDynamic() === "board.preview";
            if (boardSettings) boardSettings.hidden = !showBoardPreview;
            applyBoard();
        };

        typeSelect?.addEventListener("change", apply);
        dynamicSelect?.addEventListener("change", apply);
        boardSelect?.addEventListener("change", applyBoard);
        apply();

        initTemplateBinding(form, collectionFields);
        initCollectionPreview(form);
    });
}

/**
 * 템플릿 섹션 게시판 연동(data_binding) UI — 정적/연동 토글, 게시판 변경 시
 * 필터 패널·슬롯 소스 옵션 갱신, 슬롯 소스별 입력(필드/고정텍스트) 노출.
 */
type BindingSource = { value: string; label: string; kind: string };
type CollectionField = {
    value: string;
    label: string;
    kind: string;
    group?: string;
    relationType?: string;
    fieldType?: string;
    compatibleSlots?: string[];
    requiredRelation?: string;
    localeAware?: boolean;
};

function initTemplateBinding(form: HTMLElement, collectionFields: Record<string, CollectionField[]> = {}): void {
    const wrap = form.querySelector<HTMLElement>("[data-template-binding]");
    if (!wrap) return;

    const toggle = form.querySelector<HTMLInputElement>(
        'input[name="data_binding[enabled]"]',
    );
    const panel = form.querySelector<HTMLElement>("[data-template-binding-panel]");
    const boardSelect = form.querySelector<HTMLSelectElement>(
        "[data-template-binding-board]",
    );
    const filterPanels = Array.from(
        form.querySelectorAll<HTMLElement>("[data-template-binding-filter-panel]"),
    );
    const repeatSelect = form.querySelector<HTMLSelectElement>(
        "[data-template-binding-repeat]",
    );
    const slotGroups = Array.from(
        form.querySelectorAll<HTMLElement>("[data-template-binding-slots]"),
    );
    let slots = Array.from(
        form.querySelectorAll<HTMLElement>("[data-template-binding-slot]"),
    );
    const addSlotButton = form.querySelector<HTMLButtonElement>("[data-template-binding-add-slot]");
    const addSlotType = form.querySelector<HTMLSelectElement>("[data-template-binding-add-slot-type]");

    let sources: Record<string, BindingSource[]> = {};
    const sourcesEl = form.querySelector<HTMLScriptElement>(
        "[data-template-binding-sources]",
    );
    if (sourcesEl) {
        try {
            sources = JSON.parse(sourcesEl.textContent || "{}");
        } catch {
            sources = {};
        }
    }

    const rebuildSlotFields = (): void => {
        const boardId = boardSelect?.value || "";
        const list = sources[boardId] || [];
        slots.forEach((slotEl) => {
            const kind = slotEl.dataset.slotKind || "text";
            const select = slotEl.querySelector<HTMLSelectElement>(
                "[data-template-binding-slot-field-input]",
            );
            if (!select) return;
            const prev = select.value;
            const placeholder =
                select.querySelector<HTMLOptionElement>("option[value='']")
                    ?.textContent || "필드 선택";
            const allowed = list.filter((s) => isFieldCompatibleWithSlot(s, kind));
            select.innerHTML = "";
            const ph = document.createElement("option");
            ph.value = "";
            ph.textContent = placeholder;
            select.appendChild(ph);
            allowed.forEach((s) => {
                const opt = document.createElement("option");
                opt.value = s.value;
                opt.textContent = s.label;
                if (s.value === prev) opt.selected = true;
                select.appendChild(opt);
            });
        });
    };

    const applyBoard = (): void => {
        const boardId = boardSelect?.value || "";
        filterPanels.forEach((p) => {
            const active = boardId !== "" && p.dataset.boardId === boardId;
            p.hidden = !active;
            p.querySelectorAll<HTMLInputElement | HTMLSelectElement>(
                "[data-template-binding-filter-input]",
            ).forEach((i) => {
                i.disabled = !active;
            });
        });
        rebuildSlotFields();
        renderFieldBrowser(form, boardId, collectionFields);
    };

    const applyRepeat = (): void => {
        if (!repeatSelect) return;
        const repeat = repeatSelect.value;
        slotGroups.forEach((g) => {
            g.hidden = g.dataset.repeat !== repeat;
        });
    };

    const applySlotSource = (slotEl: HTMLElement): void => {
        const source =
            slotEl.querySelector<HTMLSelectElement>(
                "[data-template-binding-slot-source]",
            )?.value || "none";
        const fieldWrap = slotEl.querySelector<HTMLElement>(
            "[data-template-binding-slot-field]",
        );
        const fixedWrap = slotEl.querySelector<HTMLElement>(
            "[data-template-binding-slot-fixed]",
        );
        const formatWrap = slotEl.querySelector<HTMLElement>(
            "[data-template-binding-slot-format]",
        );
        if (fieldWrap) fieldWrap.hidden = source !== "field";
        if (fixedWrap) fixedWrap.hidden = source !== "fixed";
        if (formatWrap) formatWrap.hidden = source !== "field";
    };

    const applyEnabled = (): void => {
        if (panel) panel.hidden = !toggle?.checked;
    };

    toggle?.addEventListener("change", applyEnabled);
    boardSelect?.addEventListener("change", applyBoard);
    repeatSelect?.addEventListener("change", applyRepeat);
    const updateSlotOrder = (): void => {
        slotGroups.forEach((group) => {
            Array.from(group.querySelectorAll<HTMLElement>("[data-template-binding-slot]")).forEach((slotEl, index) => {
                const input = slotEl.querySelector<HTMLInputElement>("[data-template-binding-slot-order]");
                if (input) input.value = String((index + 1) * 10);
            });
        });
    };

    // ── 슬롯 상세 편집 모달 ──────────────────────────────────────────────
    // 실 컨트롤은 폼 안 hidden 으로 유지(자동저장·제출의 source of truth). 모달은
    // 값을 복제한 native 미러만 띄우고, "적용" 시 실 컨트롤에 값 write + change
    // dispatch(→ applySlotSource + 850ms 자동저장)한다. 모달 편집 중엔 폼 값 불변.
    const slotRealRefs = (slotEl: HTMLElement) => ({
        source: slotEl.querySelector<HTMLSelectElement>("[data-template-binding-slot-source]"),
        field: slotEl.querySelector<HTMLSelectElement>("[data-template-binding-slot-field-input]"),
        fixed: slotEl.querySelector<HTMLInputElement>("input[name$='[value]']"),
        formatWrap: slotEl.querySelector<HTMLElement>("[data-template-binding-slot-format]"),
    });

    const slotPathLabel = (source: string, field: string, fixed: string): { text: string; state: string } => {
        if (source === "field" && field !== "") return { text: field, state: "mapped" };
        if (source === "auto") return { text: "item.detailUrl", state: "mapped" };
        if (source === "fixed" && fixed !== "") return { text: `"${fixed}"`, state: "mapped" };
        return { text: "unmapped", state: "unmapped" };
    };

    const updateSlotChip = (slotEl: HTMLElement): void => {
        const refs = slotRealRefs(slotEl);
        const info = slotPathLabel(refs.source?.value || "none", refs.field?.value || "", refs.fixed?.value || "");
        const chip = slotEl.querySelector<HTMLElement>("[data-slot-path-chip]");
        if (chip) {
            chip.textContent = info.text;
            chip.dataset.state = info.state;
        }
    };

    const openSlotModal = (slotEl: HTMLElement): void => {
        if (document.querySelector(".cms-slot-modal-backdrop")) return;
        const refs = slotRealRefs(slotEl);
        if (!refs.source) return;
        const kind = slotEl.dataset.slotKind || "text";
        const board = boardSelect?.value || "";
        const titleText = slotEl.querySelector<HTMLElement>("[data-slot-label]")?.textContent?.trim() || "슬롯";

        const root =
            document.getElementById("app-modal-root") ??
            (() => {
                const el = document.createElement("div");
                el.id = "app-modal-root";
                document.body.appendChild(el);
                return el;
            })();
        const backdrop = document.createElement("div");
        backdrop.className = "app-modal-backdrop cms-slot-modal-backdrop";
        const box = document.createElement("div");
        box.className = "app-modal-box app-modal-box--md cms-slot-modal";
        box.innerHTML = `
            <div class="app-modal-header">
                <h2 class="app-modal-title">슬롯 편집 · ${escapeHtml(titleText)}</h2>
                <button type="button" class="app-modal-close" data-slot-modal-close aria-label="닫기"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
            </div>
            <div class="app-modal-body app-modal-body--scroll cms-slot-modal__body" data-lenis-prevent></div>
            <div class="app-modal-footer">
                <button type="button" class="app-modal-btn" data-slot-modal-close>취소</button>
                <button type="button" class="app-modal-btn app-modal-btn--primary" data-slot-modal-apply>적용</button>
            </div>`;
        const body = box.querySelector<HTMLElement>(".cms-slot-modal__body")!;

        // 소스
        const mSource = buildMirrorSelect(refs.source, "data-slot-modal-source");
        body.appendChild(slotModalField("소스", mSource));

        // 게시판 필드 + 필드 브라우저
        const mFieldWrap = document.createElement("div");
        mFieldWrap.dataset.slotModalField = "1";
        const mField = refs.field ? buildMirrorSelect(refs.field, "data-slot-modal-field-input") : null;
        if (mField) mFieldWrap.appendChild(slotModalField("게시판 필드", mField));
        const fieldBrowser = document.createElement("details");
        fieldBrowser.className = "cms-field-browser cms-slot-modal__browser";
        fieldBrowser.open = true;
        fieldBrowser.dataset.fieldBrowser = "1";
        fieldBrowser.innerHTML =
            '<summary class="cms-field-browser__summary"><span>필드 브라우저</span><i class="fa-solid fa-chevron-down" aria-hidden="true"></i></summary><div class="cms-field-browser__body"></div>';
        mFieldWrap.appendChild(fieldBrowser);
        body.appendChild(mFieldWrap);

        // 고정 텍스트
        const mFixedWrap = document.createElement("div");
        mFixedWrap.dataset.slotModalFixed = "1";
        const mFixed = document.createElement("input");
        mFixed.className = "admin-form__input";
        mFixed.value = refs.fixed?.value || "";
        mFixedWrap.appendChild(slotModalField("고정 텍스트", mFixed));
        body.appendChild(mFixedWrap);

        // 배열 Format — 실 포맷 컨트롤을 그대로 미러링(numberStepper 포함).
        const realFormatControls = refs.formatWrap
            ? Array.from(refs.formatWrap.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input[name], select[name]"))
            : [];
        const formatDetails = document.createElement("details");
        formatDetails.className = "cms-collection-slot-format cms-slot-modal__format";
        formatDetails.dataset.slotModalFormat = "1";
        const formatSummary = document.createElement("summary");
        formatSummary.textContent = "배열 Format";
        formatDetails.appendChild(formatSummary);
        const mirrorFormat: Array<{ real: HTMLInputElement | HTMLSelectElement; mirror: HTMLInputElement | HTMLSelectElement }> = [];
        realFormatControls.forEach((real) => {
            const keyLabel = (real.getAttribute("name") || "").match(/\[format\]\[([^\]]+)\]/)?.[1] || "값";
            let mirror: HTMLInputElement | HTMLSelectElement;
            if (real instanceof HTMLSelectElement) {
                mirror = buildMirrorSelect(real, "data-slot-modal-format-control");
            } else {
                const input = document.createElement("input");
                input.className = "admin-form__input";
                if (real.type === "number") {
                    input.type = "number";
                    if (real.min) input.min = real.min;
                    if (real.max) input.max = real.max;
                }
                input.value = real.value;
                mirror = input;
            }
            formatDetails.appendChild(slotModalField(keyLabel, mirror));
            mirrorFormat.push({ real, mirror });
        });
        body.appendChild(formatDetails);

        backdrop.appendChild(box);
        root.appendChild(backdrop);

        // 필드 브라우저 채우기(호환 필드만) — 표준 renderFieldBrowser 재사용.
        const compat = (collectionFields[board] || []).filter((candidate) => isFieldCompatibleWithSlot(candidate, kind));
        renderFieldBrowser(body, board, { [board]: compat });

        const applyModalSource = (): void => {
            const src = mSource.value;
            mFieldWrap.hidden = src !== "field";
            mFixedWrap.hidden = src !== "fixed";
            formatDetails.hidden = src !== "field";
        };
        mSource.addEventListener("change", applyModalSource);
        applyModalSource();

        // 필드 브라우저 클릭 → 미러에만 스테이징(적용 시 1회 저장). 모달은 폼 밖이라
        // form 위임 핸들러와 중복 발화하지 않는다.
        body.addEventListener("click", (event) => {
            const item = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-field-value]");
            if (!item || !body.contains(item) || !mField) return;
            const value = item.dataset.fieldValue || "";
            if (!Array.from(mField.options).some((option) => option.value === value)) {
                const option = document.createElement("option");
                option.value = value;
                option.textContent = item.querySelector("strong")?.textContent || value;
                mField.appendChild(option);
            }
            mSource.value = "field";
            mField.value = value;
            applyModalSource();
        });

        const close = (): void => backdrop.remove();
        box.querySelectorAll("[data-slot-modal-close]").forEach((btn) => btn.addEventListener("click", close));
        backdrop.addEventListener("click", (event) => {
            if (event.target === backdrop) close();
        });

        box.querySelector<HTMLButtonElement>("[data-slot-modal-apply]")?.addEventListener("click", () => {
            if (refs.source) {
                refs.source.value = mSource.value;
                refs.source.dispatchEvent(new Event("change", { bubbles: true }));
            }
            if (refs.field && mField) {
                if (mField.value !== "" && !Array.from(refs.field.options).some((option) => option.value === mField.value)) {
                    const option = document.createElement("option");
                    option.value = mField.value;
                    option.textContent = mField.options[mField.selectedIndex]?.textContent || mField.value;
                    refs.field.appendChild(option);
                }
                refs.field.value = mField.value;
                refs.field.dispatchEvent(new Event("change", { bubbles: true }));
            }
            if (refs.fixed) {
                refs.fixed.value = mFixed.value;
                refs.fixed.dispatchEvent(new Event("input", { bubbles: true }));
                refs.fixed.dispatchEvent(new Event("change", { bubbles: true }));
            }
            mirrorFormat.forEach(({ real, mirror }) => {
                real.value = mirror.value;
                real.dispatchEvent(new Event("input", { bubbles: true }));
                real.dispatchEvent(new Event("change", { bubbles: true }));
            });
            applySlotSource(slotEl);
            updateSlotChip(slotEl);
            close();
        });
    };

    const bindSlot = (slotEl: HTMLElement): void => {
        if (slotEl.dataset.templateBindingSlotBound === "1") return;
        slotEl.dataset.templateBindingSlotBound = "1";
        slotEl.querySelector<HTMLSelectElement>("[data-template-binding-slot-source]")?.addEventListener("change", () => applySlotSource(slotEl));
        slotEl.querySelector<HTMLButtonElement>("[data-template-binding-slot-edit]")?.addEventListener("click", () => openSlotModal(slotEl));
        slotEl.querySelectorAll<HTMLButtonElement>("[data-template-binding-slot-action]").forEach((button) => {
            button.addEventListener("click", () => {
                button.closest("details")?.removeAttribute("open");
                handleSlotAction(slotEl, button.dataset.templateBindingSlotAction || "", collectionFields[boardSelect?.value || ""] || [], applySlotSource);
                slots = Array.from(form.querySelectorAll<HTMLElement>("[data-template-binding-slot]"));
                slots.forEach(bindSlot);
                rebuildSlotFields();
                updateSlotOrder();
            });
        });
        applySlotSource(slotEl);
    };
    const assignFieldToSelectedSlot = (fieldValue: string): void => {
        const selectedSlotKey = form.querySelector<HTMLElement>("[data-selected-collection-slot]")?.dataset.selectedCollectionSlot || "";
        if (selectedSlotKey === "") return;
        const slotEl = form.querySelector<HTMLElement>(`[data-template-binding-slot][data-slot-key="${CSS.escape(selectedSlotKey)}"]`);
        if (!slotEl) return;
        const kind = slotEl.dataset.slotKind || "text";
        const field = (collectionFields[boardSelect?.value || ""] || []).find((candidate) => candidate.value === fieldValue);
        if (!field || !isFieldCompatibleWithSlot(field, kind)) return;
        const sourceSelect = slotEl.querySelector<HTMLSelectElement>("[data-template-binding-slot-source]");
        const fieldSelect = slotEl.querySelector<HTMLSelectElement>("[data-template-binding-slot-field-input]");
        if (!sourceSelect || !fieldSelect) return;
        if (!Array.from(fieldSelect.options).some((option) => option.value === fieldValue)) {
            const option = document.createElement("option");
            option.value = fieldValue;
            option.textContent = field.label || fieldValue;
            fieldSelect.appendChild(option);
        }
        sourceSelect.value = "field";
        fieldSelect.value = fieldValue;
        applySlotSource(slotEl);
        updateSlotChip(slotEl);
        slotEl.classList.add("is-selected-slot");
        fieldSelect.dispatchEvent(new Event("change", { bubbles: true }));
    };
    form.addEventListener("click", (event) => {
        const item = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-field-value]");
        if (!item || !form.contains(item)) return;
        assignFieldToSelectedSlot(item.dataset.fieldValue || "");
    });
    slots.forEach(bindSlot);
    addSlotButton?.addEventListener("click", () => {
        const activeGroup = slotGroups.find((group) => !group.hidden) || slotGroups[0];
        if (!activeGroup) return;
        const type = addSlotType?.value || "text";
        const key = uniqueSlotKey(activeGroup, type);
        const slotEl = createCollectionSlotElement(key, type, collectionFields[boardSelect?.value || ""] || []);
        activeGroup.appendChild(slotEl);
        slots.push(slotEl);
        bindSlot(slotEl);
        rebuildSlotFields();
        updateSlotOrder();
    });

    applyEnabled();
    applyRepeat();
    rebuildSlotFields();
    updateSlotOrder();
    renderFieldBrowser(form, boardSelect?.value || "", collectionFields);
}

function handleSlotAction(
    slotEl: HTMLElement,
    action: string,
    fields: CollectionField[],
    applySlotSource: (slotEl: HTMLElement) => void,
): void {
    const group = slotEl.parentElement;
    if (!group) return;
    if (action === "up") {
        const previous = slotEl.previousElementSibling;
        if (previous) group.insertBefore(slotEl, previous);
        return;
    }
    if (action === "down") {
        const next = slotEl.nextElementSibling;
        if (next) group.insertBefore(next, slotEl);
        return;
    }
    if (action === "hide") {
        const input = slotEl.querySelector<HTMLInputElement>("[data-template-binding-slot-hidden]");
        const nextHidden = input?.value !== "1";
        if (input) input.value = nextHidden ? "1" : "0";
        slotEl.classList.toggle("is-hidden-slot", nextHidden);
        return;
    }
    if (action === "delete") {
        slotEl.remove();
        return;
    }
    if (action === "duplicate") {
        const type = slotTypeFromElement(slotEl);
        const key = uniqueSlotKey(group, type);
        const copy = createCollectionSlotElement(key, type, fields);
        group.insertBefore(copy, slotEl.nextSibling);
        copySlotValues(slotEl, copy);
        applySlotSource(copy);
    }
}

function slotTypeFromElement(slotEl: HTMLElement): string {
    const typeInput = slotEl.querySelector<HTMLInputElement>("input[name^='data_binding[extra_slots]'][name$='[type]']");
    if (typeInput?.value) return typeInput.value;
    const kind = slotEl.dataset.slotKind || "text";
    return kind === "media" ? "image" : kind === "url" ? "link" : kind === "textarea" ? "rich_text" : kind;
}

function copySlotValues(from: HTMLElement, to: HTMLElement): void {
    const source = from.querySelector<HTMLSelectElement>("[data-template-binding-slot-source]")?.value || "none";
    const field = from.querySelector<HTMLSelectElement>("[data-template-binding-slot-field-input]")?.value || "";
    const fixed = from.querySelector<HTMLInputElement>("input[name$='[value]']")?.value || "";
    const format = from.querySelector<HTMLSelectElement>("select[name$='[format][type]']")?.value || "join";
    const toSource = to.querySelector<HTMLSelectElement>("[data-template-binding-slot-source]");
    const toField = to.querySelector<HTMLSelectElement>("[data-template-binding-slot-field-input]");
    const toFixed = to.querySelector<HTMLInputElement>("input[name$='[value]']");
    const toFormat = to.querySelector<HTMLSelectElement>("select[name$='[format][type]']");
    if (toSource) toSource.value = source;
    if (toField) toField.value = field;
    if (toFixed) toFixed.value = fixed;
    if (toFormat) toFormat.value = format;
}

function uniqueSlotKey(group: HTMLElement, type: string): string {
    const base = type === "repeater" ? "tags" : type.replace(/[^a-z0-9_]+/gi, "_");
    const used = new Set(
        Array.from(group.querySelectorAll<HTMLElement>("[data-template-binding-slot]")).map((slot) => {
            const input = slot.querySelector<HTMLInputElement>("input[name^='data_binding[extra_slots]']");
            const match = input?.name.match(/extra_slots]\[([^\]]+)]/);
            return match?.[1] || "";
        }),
    );
    let key = base || "slot";
    let index = 1;
    while (used.has(key) || group.querySelector(`[name="data_binding[slot_map][${CSS.escape(key)}][source]"]`)) {
        index += 1;
        key = `${base}_${index}`;
    }
    return key;
}

const SLOT_ROLE_ICON: Record<string, string> = {
    Text: "fa-solid fa-font",
    Image: "fa-solid fa-image",
    Link: "fa-solid fa-link",
    Repeater: "fa-solid fa-layer-group",
};

function slotRoleInfo(type: string, kind: string): { label: string; icon: string } {
    const label =
        kind === "media"
            ? "Image"
            : kind === "url"
              ? "Link"
              : type === "repeater" || kind === "repeater"
                ? "Repeater"
                : "Text";
    return { label, icon: SLOT_ROLE_ICON[label] || SLOT_ROLE_ICON.Text };
}

/** ⋯ 오버플로 메뉴 — 서버 마크업(cms-section-form.php)과 동일한 훅/구조. */
function slotMenuHtml(): string {
    return `
        <details class="cms-collection-template-map__slot-more">
          <summary aria-label="슬롯 작업"><i class="fa-solid fa-ellipsis" aria-hidden="true"></i></summary>
          <div class="cms-collection-template-map__slot-menu">
            <button type="button" data-template-binding-slot-action="up"><i class="fa-solid fa-arrow-up" aria-hidden="true"></i><span>위로 이동</span></button>
            <button type="button" data-template-binding-slot-action="down"><i class="fa-solid fa-arrow-down" aria-hidden="true"></i><span>아래로 이동</span></button>
            <button type="button" data-template-binding-slot-action="duplicate"><i class="fa-regular fa-clone" aria-hidden="true"></i><span>복제</span></button>
            <button type="button" data-template-binding-slot-action="hide"><i class="fa-regular fa-eye-slash" aria-hidden="true"></i><span>숨김/표시</span></button>
            <button type="button" class="is-danger" data-template-binding-slot-action="delete"><i class="fa-regular fa-trash-can" aria-hidden="true"></i><span>삭제</span></button>
          </div>
        </details>`;
}

/** 실제 소스/필드/고정/포맷 컨트롤(폼 제출·자동저장의 source of truth). 항상 hidden 유지. */
function slotEditorInnerHtml(key: string, kind: string, fields: CollectionField[]): string {
    const k = escapeAttr(key);
    return `
        <label class="admin-form__field"><span class="admin-form__label">소스</span><select class="admin-form__input" name="data_binding[slot_map][${k}][source]" data-template-binding-slot-source="1"><option value="none">없음</option><option value="field">게시판 필드</option><option value="fixed">고정 텍스트</option></select></label>
        <div data-template-binding-slot-field hidden><label class="admin-form__field"><span class="admin-form__label">게시판 필드</span><select class="admin-form__input" name="data_binding[slot_map][${k}][field]" data-template-binding-slot-field-input="1"><option value="">필드 선택</option>${fieldOptionsHtml(kind, fields)}</select></label></div>
        <div data-template-binding-slot-fixed hidden><label class="admin-form__field"><span class="admin-form__label">고정 텍스트</span><input class="admin-form__input" name="data_binding[slot_map][${k}][value]" value=""></label></div>
        <details class="cms-collection-slot-format" data-template-binding-slot-format hidden>
          <summary>배열 Format</summary>
          <label class="admin-form__field"><span class="admin-form__label">Format</span><select class="admin-form__input" name="data_binding[slot_map][${k}][format][type]"><option value="join">join</option><option value="chips">chips</option><option value="repeat">repeat</option><option value="first">first</option><option value="last">last</option><option value="count">count</option><option value="hidden_if_empty">hidden_if_empty</option></select></label>
          <label class="admin-form__field"><span class="admin-form__label">separator</span><input class="admin-form__input" name="data_binding[slot_map][${k}][format][separator]" value=", "></label>
          <label class="admin-form__field"><span class="admin-form__label">maxItems</span><input class="admin-form__input" type="number" min="0" max="24" name="data_binding[slot_map][${k}][format][maxItems]" value="0"></label>
          <label class="admin-form__field"><span class="admin-form__label">overflowLabel</span><input class="admin-form__input" name="data_binding[slot_map][${k}][format][overflowLabel]" value="+{count}"></label>
        </details>`;
}

function createCollectionSlotElement(key: string, type: string, fields: CollectionField[]): HTMLElement {
    const kind = type === "rich_text" ? "textarea" : type === "image" ? "media" : type === "link" ? "url" : type;
    const label = `${type.replace("_", " ")} ${key}`;
    const role = slotRoleInfo(type, kind);
    const slot = document.createElement("div");
    slot.className = "cms-template-binding__slot";
    slot.dataset.templateBindingSlot = "1";
    slot.dataset.slotKind = kind;
    slot.dataset.slotKey = key;
    slot.dataset.extraSlot = "1";
    slot.innerHTML = `
        <input type="hidden" name="data_binding[extra_slots][${escapeAttr(key)}][type]" value="${escapeAttr(type)}">
        <input type="hidden" name="data_binding[extra_slots][${escapeAttr(key)}][label]" value="${escapeAttr(label)}">
        <input type="hidden" name="data_binding[extra_slots][${escapeAttr(key)}][hidden]" value="0" data-template-binding-slot-hidden>
        <input type="hidden" name="data_binding[extra_slots][${escapeAttr(key)}][order]" value="0" data-template-binding-slot-order>
        <div class="cms-collection-template-map__slot-row">
          <span class="cms-collection-template-map__slot-icon" aria-hidden="true"><i class="${role.icon}"></i></span>
          <span class="cms-collection-template-map__slot-meta">
            <strong data-slot-label>${escapeHtml(role.label)} · ${escapeHtml(label)}</strong>
            <code data-slot-path-chip data-state="unmapped">unmapped</code>
          </span>
          <button type="button" class="admin-btn admin-btn--xs admin-btn--outline" data-template-binding-slot-edit>편집</button>
          ${slotMenuHtml()}
        </div>
        <div class="cms-collection-template-map__slot-editor" data-template-binding-slot-editor hidden>
          ${slotEditorInnerHtml(key, kind, fields)}
        </div>
    `;
    return slot;
}

/** app-modal 스타일 인풋(.admin-form__field)로 감싸는 헬퍼. */
function slotModalField(labelText: string, control: HTMLElement): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "admin-form__field";
    const lab = document.createElement("span");
    lab.className = "admin-form__label";
    lab.textContent = labelText;
    wrap.append(lab, control);
    return wrap;
}

/** 실 컨트롤의 옵션/값을 그대로 복제한 native 미러 셀렉트(모달용). */
function buildMirrorSelect(real: HTMLSelectElement, dataAttr: string): HTMLSelectElement {
    const sel = document.createElement("select");
    sel.className = "admin-form__input";
    sel.setAttribute(dataAttr, "1");
    Array.from(real.options).forEach((option) => sel.appendChild(option.cloneNode(true)));
    sel.value = real.value;
    return sel;
}

function fieldOptionsHtml(kind: string, fields: CollectionField[]): string {
    return fields
        .filter((field) => isFieldCompatibleWithSlot(field, kind))
        .map((field) => `<option value="${escapeAttr(field.value)}">${escapeHtml(field.label || field.value)}</option>`)
        .join("");
}

function isFieldCompatibleWithSlot(field: CollectionField, slotKind: string): boolean {
    const slot = slotKind === "media" ? "image" : slotKind === "url" ? "link" : slotKind === "textarea" ? "rich_text" : slotKind;
    if (Array.isArray(field.compatibleSlots) && field.compatibleSlots.length > 0) {
        return field.compatibleSlots.includes(slot);
    }
    return slot === "image" ? field.kind === "image" : field.kind !== "image";
}

function escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[ch] || ch));
}

function escapeAttr(value: string): string {
    return escapeHtml(value).replace(/`/g, "&#096;");
}

function readCollectionFields(form: HTMLElement): Record<string, CollectionField[]> {
    const el = form.querySelector<HTMLScriptElement>("[data-collection-fields]");
    if (!el) return {};
    try {
        const parsed = JSON.parse(el.textContent || "{}") as Record<string, CollectionField[]>;
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

function renderFieldBrowser(
    form: HTMLElement,
    boardId: string,
    fieldsByBoard: Record<string, CollectionField[]>,
): void {
    const browsers = Array.from(form.querySelectorAll<HTMLElement>("[data-field-browser]"));
    const fields = fieldsByBoard[boardId] || [];
    browsers.forEach((browser) => {
        browser.dataset.fieldBrowserBoard = boardId;
        const body = browser.querySelector<HTMLElement>(".cms-field-browser__body");
        if (!body) return;
        body.replaceChildren();
        if (fields.length === 0) {
            const hint = document.createElement("p");
            hint.className = "admin-form__hint cms-template-binding__empty";
            hint.textContent = boardId === "" ? "게시판을 선택하면 매핑 가능한 필드가 표시됩니다." : "이 게시판에서 표시할 필드를 찾지 못했습니다.";
            body.appendChild(hint);
            return;
        }
        const search = document.createElement("label");
        search.className = "cms-field-browser__search";
        const searchLabel = document.createElement("span");
        searchLabel.textContent = "검색";
        const searchInput = document.createElement("input");
        searchInput.type = "search";
        searchInput.placeholder = "item.translation.title";
        searchInput.dataset.fieldBrowserSearch = "1";
        search.append(searchLabel, searchInput);
        const list = document.createElement("div");
        list.className = "cms-field-browser__list";
        let currentGroup = "";
        fields.forEach((field) => {
            const group = field.group || field.relationType || "필드";
            if (group !== currentGroup) {
                const groupLabel = document.createElement("strong");
                groupLabel.className = "cms-field-browser__group";
                groupLabel.textContent = group;
                list.appendChild(groupLabel);
                currentGroup = group;
            }
            const item = document.createElement("button");
            item.type = "button";
            item.className = "cms-field-browser__item";
            item.dataset.kind = field.kind || "text";
            item.dataset.fieldValue = field.value || "";
            const compatibleSlots = Array.isArray(field.compatibleSlots) ? field.compatibleSlots.join(", ") : "";
            item.dataset.fieldSearch = `${field.label || ""} ${field.value || ""} ${field.relationType || ""} ${field.fieldType || ""} ${compatibleSlots} ${field.requiredRelation || ""}`.toLowerCase();
            const label = document.createElement("strong");
            label.textContent = field.label || field.value;
            const code = document.createElement("code");
            code.textContent = field.value;
            const relation = document.createElement("em");
            relation.textContent = [
                field.relationType || "Scalar",
                field.fieldType || "",
                compatibleSlots ? `slots: ${compatibleSlots}` : "",
                field.requiredRelation ? `relation: ${field.requiredRelation}` : "",
                field.localeAware ? "locale" : "",
            ].filter(Boolean).join(" · ");
            item.append(label, code, relation);
            list.appendChild(item);
        });
        searchInput.addEventListener("input", () => filterFieldBrowser(browser));
        body.append(search, list);
    });
}

function filterFieldBrowser(browser: HTMLElement): void {
    const input = browser.querySelector<HTMLInputElement>("[data-field-browser-search]");
    const term = (input?.value || "").trim().toLowerCase();
    browser.querySelectorAll<HTMLElement>(".cms-field-browser__item").forEach((item) => {
        item.hidden = term !== "" && !(item.dataset.fieldSearch || "").includes(term);
    });
}

function initCollectionPreview(form: HTMLElement): void {
    const wrap = form.querySelector<HTMLElement>("[data-collection-preview]");
    if (!wrap || wrap.dataset.collectionPreviewInited === "1") return;
    wrap.dataset.collectionPreviewInited = "1";
    const button = wrap.querySelector<HTMLButtonElement>("[data-collection-preview-trigger]");
    const result = wrap.querySelector<HTMLElement>("[data-collection-preview-result]");
    const url = wrap.dataset.previewUrl || "";
    if (!button || !result || url === "") return;

    button.addEventListener("click", async () => {
        const original = button.innerHTML;
        button.disabled = true;
        button.innerHTML = '<i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i><span>조회 중</span>';
        result.textContent = "";
        try {
            const formElement =
                form instanceof HTMLFormElement
                    ? form
                    : (form.closest("form") as HTMLFormElement | null);
            const payload = formElement ? new FormData(formElement) : new FormData();
            payload.delete("_method");
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
                body: payload,
            });
            const json = (await response.json().catch(() => ({}))) as {
                result?: string;
                msg?: string;
                state?: string;
                items?: Array<Record<string, unknown>>;
                meta?: Record<string, unknown>;
            };
            if (!response.ok || json.result !== "success") {
                renderPreviewMessage(result, json.msg || "조회 결과를 불러오지 못했습니다.");
                return;
            }
            renderPreviewItems(result, json.items || [], json.meta || {});
        } catch {
            renderPreviewMessage(result, "네트워크 오류로 미리보기를 불러오지 못했습니다.");
        } finally {
            button.disabled = false;
            button.innerHTML = original;
        }
    });
}

function renderPreviewMessage(result: HTMLElement, message: string): void {
    result.replaceChildren();
    const p = document.createElement("p");
    p.className = "admin-form__hint";
    p.textContent = message;
    result.appendChild(p);
}

function renderPreviewItems(
    result: HTMLElement,
    items: Array<Record<string, unknown>>,
    meta: Record<string, unknown>,
): void {
    result.replaceChildren();
    if (items.length === 0) {
        renderPreviewMessage(result, "현재 조건에 해당하는 게시물이 없습니다.");
        return;
    }
    const summary = document.createElement("p");
    summary.className = "cms-collection-preview__summary";
    summary.textContent = `총 ${String(meta.total ?? items.length)}개 중 ${items.length}개 미리보기`;
    const list = document.createElement("div");
    list.className = "cms-collection-preview__list";
    items.forEach((item) => {
        const card = document.createElement("article");
        card.className = "cms-collection-preview__item";
        const title = document.createElement("strong");
        title.textContent = String(item.title || "제목 없음");
        const metaLine = document.createElement("span");
        const tags = Array.isArray(item.tags) ? item.tags.map(String).join(", ") : "";
        metaLine.textContent = [item.category, tags, item.date].filter(Boolean).map(String).join(" · ");
        const url = document.createElement("code");
        url.textContent = String(item.detailUrl || "");
        card.append(title, metaLine, url);
        list.appendChild(card);
    });
    result.append(summary, list);
}
